import {
  EventContentArg,
  EventSourceFuncArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";

import ContainerAnimation from "components/ContainerAnimation";
import { useAuth } from "providers/Auth";
import { useCompany } from "providers/Company";
import { useUser } from "providers/User";
import { api } from "services";
import { RoleDescription } from "types/enums";
import { formatPrice } from "utils/formats";
import { REPASSE_BUSINESS_DAYS } from "utils/repasse";
import { toast } from "utils/toast";

import CompanySelect from "./components/CompanySelect";
import ProfessionalSelect from "./components/ProfessionalSelect";
import RepasseCalendar from "./components/RepasseCalendar";
import * as S from "./styles";

type ActiveTab = "procedimentos" | "repasse";

const Schedule = () => {
  const { userRole } = useAuth();
  const { userData } = useUser();
  const {
    professionalCompanies,
    isLoadingProfessionalCompanies,
    getProfessionalCompanies,
  } = useCompany();

  useEffect(() => {
    if (
      userData.id &&
      (userRole === RoleDescription.Professional ||
        userRole === RoleDescription.Secretary)
    ) {
      getProfessionalCompanies(userData.id as number);
    }
  }, [userData]);

  const [companyId, setCompanyId] = useState<number>(-1);
  const [professionalId, setProfessionalId] = useState<number>(-1);
  const [activeTab, setActiveTab] = useState<ActiveTab>("procedimentos");

  const [monthPaid, setMonthPaid] = useState(0);
  const [monthForecast, setMonthForecast] = useState(0);
  const [repasseAgendado, setRepasseAgendado] = useState(0);
  const [repasseRepassado, setRepasseRepassado] = useState(0);
  const [isLoadingProcCalendar, setIsLoadingProcCalendar] = useState(false);

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>, tab: ActiveTab) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveTab(tab);
    }
  };

  // Identidade estável: se a função `events` muda a cada render, o
  // FullCalendar a trata como nova fonte e refaz o fetch em loop.
  const fetchProcEvents = useCallback(
    async (
      info: EventSourceFuncArg,
      successCallback: (events: EventInput[]) => void,
      failureCallback: (error: Error) => void
    ) => {
      try {
        const { data } = await api.post("/Payment/Forecasting", {
          startDate: info.startStr,
          endDate: info.endStr,
          companyId: companyId === -1 ? undefined : companyId,
          professionalId: professionalId === -1 ? undefined : professionalId,
        });

        let paid = 0;
        let forecast = 0;

        const events = data.map((item: any) => {
          paid += item.totalPaidValue ?? 0;
          forecast += item.totalForecastValue ?? 0;
          return {
            start: item.date,
            extendedProps: {
              totalPaidValue: item.totalPaidValue,
              totalForecastValue: item.totalForecastValue,
            },
          };
        });

        setMonthPaid(paid);
        setMonthForecast(forecast);
        successCallback(events);
      } catch (error) {
        toast.fire({
          icon: "error",
          title: "Não foi possível carregar o calendário. Tente novamente.",
        });
        failureCallback(error as Error);
      }
    },
    [companyId, professionalId]
  );

  const handleRepasseMonthChange = useCallback(
    (agendado: number, repassado: number) => {
      setRepasseAgendado(agendado);
      setRepasseRepassado(repassado);
    },
    []
  );

  function renderEventContent(eventInfo: EventContentArg) {
    const totalPaid = eventInfo.event._def.extendedProps.totalPaidValue ?? 0;
    const totalForecast = eventInfo.event._def.extendedProps.totalForecastValue ?? 0;

    if (!totalPaid && !totalForecast) return null;

    return (
      <Link
        to="/agenda/agenda-detalhes"
        state={{
          date: eventInfo.event.start,
          totalValue: totalPaid + totalForecast,
          companyId,
          professionalId,
        }}
        style={{ textDecoration: "none", display: "block", width: "100%" }}
      >
        <S.EventWrapper role="button" aria-label="Ver procedimentos do dia">
          {totalPaid > 0 && (
            <S.EventPill $type="paid">
              <i className="uil uil-check-circle" aria-hidden="true" />
              {formatPrice(totalPaid)}
            </S.EventPill>
          )}
          {totalForecast > 0 && (
            <S.EventPill $type="forecast">
              <i className="uil uil-clock" aria-hidden="true" />
              {formatPrice(totalForecast)} prev.
            </S.EventPill>
          )}
        </S.EventWrapper>
      </Link>
    );
  }

  // Mostra os resumos mesmo zerados — mês sem valores também é informação
  const showProcedSummary = activeTab === "procedimentos";
  const showRepasseSummary = activeTab === "repasse";

  return (
    <ContainerAnimation className="content">
      {/* Header */}
      <div className="bg-white">
        <div className="container">
          <div className="row justify-content-between align-items-center gx-2 pt-3 pb-0">
            {/* Title */}
            <div className="col-12 col-md-auto pb-2">
              <h1 className="mb-0">Calendário</h1>
            </div>

            {/* Filters */}
            <div className="col-12 col-sm-6 col-md-auto pb-2">
              <CompanySelect
                isLoadingProfessionalCompanies={isLoadingProfessionalCompanies}
                professionalCompanies={professionalCompanies}
                setCompanyId={setCompanyId}
                setProfessionalId={setProfessionalId}
              />
            </div>
            {(userRole === RoleDescription.Secretary ||
              userRole === RoleDescription.Admin) && (
              <div className="col-12 col-sm-6 col-md-auto pb-2">
                <ProfessionalSelect
                  companyId={companyId}
                  setProfessionalId={setProfessionalId}
                />
              </div>
            )}
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <span
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === "procedimentos"}
                aria-controls="tab-procedimentos"
                className={`nav-link link-primary ${activeTab === "procedimentos" ? "active" : ""}`}
                onClick={() => setActiveTab("procedimentos")}
                onKeyDown={(e) => handleTabKeyDown(e, "procedimentos")}
                style={{ cursor: "pointer" }}
              >
                <i className="uil uil-calendar-alt me-1" aria-hidden="true" />
                Procedimentos
              </span>
            </li>
            <li className="nav-item" role="presentation">
              <span
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === "repasse"}
                aria-controls="tab-repasse"
                className={`nav-link link-primary ${activeTab === "repasse" ? "active" : ""}`}
                onClick={() => setActiveTab("repasse")}
                onKeyDown={(e) => handleTabKeyDown(e, "repasse")}
                style={{ cursor: "pointer" }}
              >
                <i className="uil uil-money-bill me-1" aria-hidden="true" />
                Repasse
                <Badge bg="primary" className="ms-1">novo</Badge>
              </span>
            </li>
          </ul>

          {/* Summary strip — inside the white header, below tabs */}
          {showProcedSummary && (
            <S.SummaryStrip aria-label="Resumo do mês">
              <S.SummaryChip $variant="paid">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">Pago</span>
                <span className="chip-value">{formatPrice(monthPaid)}</span>
              </S.SummaryChip>
              <S.SummaryChip $variant="forecast">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">Previsto</span>
                <span className="chip-value">{formatPrice(monthForecast)}</span>
              </S.SummaryChip>
              <S.SummaryChip $variant="total">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">Total</span>
                <span className="chip-value">{formatPrice(monthPaid + monthForecast)}</span>
              </S.SummaryChip>
            </S.SummaryStrip>
          )}

          {showRepasseSummary && (
            <S.SummaryStrip aria-label="Resumo de repasses do mês">
              <S.SummaryChip $variant="forecast">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">A repassar</span>
                <span className="chip-value">{formatPrice(repasseAgendado)}</span>
              </S.SummaryChip>
              <S.SummaryChip $variant="paid">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">Já repassado</span>
                <span className="chip-value">{formatPrice(repasseRepassado)}</span>
              </S.SummaryChip>
              <S.SummaryChip $variant="total">
                <span className="chip-dot" aria-hidden="true" />
                <span className="chip-label">Total do mês</span>
                <span className="chip-value">{formatPrice(repasseAgendado + repasseRepassado)}</span>
              </S.SummaryChip>
            </S.SummaryStrip>
          )}
        </div>
      </div>

      {/* Calendar panels */}
      <div className="container pt-4">
        <div
          id="tab-procedimentos"
          role="tabpanel"
          aria-labelledby="tab-procedimentos"
          hidden={activeTab !== "procedimentos"}
        >
          <S.CalendarShell>
          {isLoadingProcCalendar && (
            <S.CalendarLoading aria-label="Carregando calendário">
              <div className="spinner-border text-primary" role="status" />
            </S.CalendarLoading>
          )}
          <FullCalendar
            plugins={[dayGridPlugin]}
            locale="pt-br"
            timeZone="UTC"
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,dayGridWeek,dayGridDay",
            }}
            buttonText={{ today: "Hoje", month: "Mês", week: "Semana", day: "Dia" }}
            buttonHints={{
              prev: "Anterior",
              next: "Próximo",
              today: "Hoje",
              month: "Mês",
              week: "Semana",
              day: "Dia",
            }}
            height="auto"
            loading={setIsLoadingProcCalendar}
            events={fetchProcEvents}
            eventContent={renderEventContent}
            eventColor="transparent"
          />
          </S.CalendarShell>
        </div>

        <div
          id="tab-repasse"
          role="tabpanel"
          aria-labelledby="tab-repasse"
          hidden={activeTab !== "repasse"}
        >
          <S.RuleHint>
            <i className="uil uil-info-circle" aria-hidden="true" />
            Os valores aparecem no dia do crédito — D+{REPASSE_BUSINESS_DAYS}{" "}
            dias úteis após o pagamento (pagamento + split). Clique no dia
            para ver os orçamentos e gerar o CNAB 240.
          </S.RuleHint>
          <RepasseCalendar
            companyId={companyId}
            professionalId={professionalId}
            onMonthChange={handleRepasseMonthChange}
          />
        </div>
      </div>
    </ContainerAnimation>
  );
};

export default Schedule;
